import AddLocationIcon from '@material-ui/icons/AddLocation';
import {Pre} from "evergreen-ui";
import {PAGE, splitAndRemoveEmpty} from 'placement/const';
import {ClientProviderPreference, Offer, OfferStatus, PsychotherapyAuthorizedHour} from 'placement/domains';
import pluralize from 'pluralize';
import React from 'react';
import {ESBA_DATE_FORMAT} from "../../../common";
import {Miles} from "../offer-detail/Miles";
import {Distances} from "../offer-detail/Distances";
import {SlaBadge} from '../offer-detail/SlaBadge';
import {ProviderOfferDetailItemBanners} from "./ProviderOfferDetailItemBanners";
import {
    isTreatmentOfferedCareMedicated,
    isTreatmentOfferedPractitionerMedicated,
    TreatmentOffered,
    treatmentOfferedToLabel
} from "../../../referral";
import {formatDate, formatISODateStr, isValidDate} from "common/util/date";
import {ServiceLocationTypeMap} from "offer";
import {
    CaregiverLocationDisplay,
    caregiverLocationDisplay,
    offerCaregiverLocations
} from "offer/domain/offer";
import {OfferConversationPopup} from 'offer/component/conversation';
import {isEmpty, uniq} from "lodash";
import {FeatureFlagSwitch, ProviderPortalFeatureFlag} from 'featureflag';

function findPreferredOffers(preferences: ClientProviderPreference[]) {
    if (preferences.find(p => p.clientPreference === 'Excluded_Provider')) {
        return [];
    }
    return preferences.filter(p => p.clientPreference === 'Preferred_Provider');
}

type Props = {
    isSelected: boolean;
    setSelect: () => void;
    showReturnModal: () => void;
    isMatched: boolean;
    offer: Offer;
    recentPage: PAGE;
    referralRequiredLanguage: String;
    preferences: ClientProviderPreference[];
    shortMode?: boolean,
    psychotherapyAuthorizedHour?: PsychotherapyAuthorizedHour | null;
}

export function ProviderOfferDetailItem({
                                            offer,
                                            isSelected,
                                            setSelect,
                                            isMatched,
                                            showReturnModal,
                                            recentPage,
                                            referralRequiredLanguage,
                                            psychotherapyAuthorizedHour = null,
                                            preferences = [],
                                            shortMode = false
                                        }: Props) {
    let borderClass = '';
    let subColor = 'text-gray-600';
    let hoverColor = 'hover:bg-gray-200';
    let bgStyle = 'bg-white';

    const preferred = findPreferredOffers(preferences);
    const locationCond = caregiverLocationDisplay(
        offerCaregiverLocations(offer),
        offer.treatmentOffered);
    // A Treatment-Only caregiver offer stores no assessment location, so fall back to the
    // per-level ones. This array holds stored names like ClinicCenter, not labels like
    // Clinic/Center.
    let serviceLocations = offer.serviceLocationType ? splitAndRemoveEmpty(offer.serviceLocationType) : [];
    if (serviceLocations.length === 0 && (locationCond.showMidLevel || locationCond.showHighLevel)) {
        serviceLocations = uniq([
            offer.serviceLocationMidLevelType,
            offer.serviceLocationHighLevelType,
        ].filter(Boolean));
    }
    const isPreferred = preferred.length > 0;
    if (isPreferred) {
        // lowest priority, should be overridden below by higher priority status banner
        borderClass = 'border border-indigo-500';
    }

    // see https://easterseals.atlassian.net/browse/BHPNPP-492?focusedCommentId=183905
    // a returned Offer is a declined Offer
    const isReturned = offer.status === OfferStatus.DECLINED;
    if (offer.status === OfferStatus.ACCEPTED) {
        isMatched = true;
    }
    if (isMatched) {
        borderClass = 'border border-atlantis-200';
    }
    if (isReturned) {
        borderClass = 'border border-red-600';
    }

    const isDeclinedFromTransfer = offer.declinedReason === 'Returned/Transfer Request';
    if (isDeclinedFromTransfer) {
        borderClass = 'border border-red-600';
    }

    if (isSelected) {
        subColor = '';
        hoverColor = '';
        bgStyle = 'bg-botticelli-100 text-white';
    }

    return (
        <div className={`pb-5  relative ${shortMode ? 'placement-provider-offer-detail-item-short' : ''}`}
             onClick={setSelect}
             data-testid="provider-offer-detail-item"
        >
            <ProviderOfferDetailItemBanners
                isReturned={isReturned}
                isMatched={isMatched}
                preferences={preferred}
                isDeclinedFromTransfer={isDeclinedFromTransfer}
            />

            <div
                className={`placement-provider-offer-detail-item-height shadow-md flex relative ${shortMode ? 'flex-col' : ''} p-5 ${hoverColor}  ${bgStyle} ${borderClass}`}>
                {
                    isMatched && recentPage === PAGE.COMPLETE_REFERRALS &&
                    <div
                        className='underline text-red-700 absolute font-semibold z-10 cursor-pointer provider-offer-returned-text-poisition'
                        onClick={() => showReturnModal()}>Return Offer</div>
                }
                <FeatureFlagSwitch flag={ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED} negate>
                    <div className='absolute bottom-0 right-0 mb-3 mr-4 z-10'>
                        <OfferConversationPopup
                            offerGuid={offer.guid}
                            conversationSummary={offer.conversationSummary}
                            readOnly={offer.status === OfferStatus.ACCEPTED}
                        />
                    </div>
                </FeatureFlagSwitch>
                <div className={`${shortMode ? '' : 'w-4/12'} leading-5`}>
                    <p className='text-lg font-semibold pb-2'>
                        {offer.name}
                    </p>
                    <div>
                        {
                            shortMode && serviceLocations.length > 0 &&
                            <p className="pb-1">
                                Service
                                location: {serviceLocations.map((i) => ServiceLocationTypeMap.get(i)).join(';')}
                            </p>
                        }
                        {offer.city && shortMode ?
                            <span className={`${subColor} pb-2`}>
                                <span>{offer.city}</span>
                                <Distances miles={offer.distanceToClinic?.distanceInMiles} time={offer.distanceToClinic?.timeInMillis}/>
                            </span> :
                            <p className={`${subColor} pb-2`}>
                                <span>{offer.city}</span>
                                <Distances miles={offer.distanceToClinic?.distanceInMiles} time={offer.distanceToClinic?.timeInMillis}/>
                            </p>
                        }
                        {shortMode ?
                            <span className={`${subColor} pb-2 ml-2`}>
                                {
                                    offer.address ?
                                        <React.Fragment>
                                            <AddLocationIcon/>
                                            <span className='ml-2'>{offer.address}</span>
                                        </React.Fragment> :
                                        <span/>
                                }
                            </span> :
                            <div className={`${subColor} pb-2 `}>
                                {offer.address ?
                                    <React.Fragment>
                                        <AddLocationIcon/>
                                        <span>{offer.address}</span>
                                    </React.Fragment> :
                                    <span/>
                                }
                                <p className={`${subColor} pb-2`}>
                                    {offer.patientCountLast90days} {pluralize('Client', offer.patientCountLast90days)} {pluralize('was', offer.patientCountLast90days)} placed
                                    in the last 90 days
                                </p>

                                {
                                    serviceLocations.length > 0 &&
                                    <p className="pb-1">
                                        Service
                                        location: {serviceLocations.map((i) => ServiceLocationTypeMap.get(i)).join(';')}
                                    </p>
                                }
                                <p className={`${subColor}`}>
                                    Submitted
                                    by {offer.submittedBy} on {formatISODateStr(offer.submittedAt)} at {formatISODateStr(offer.submittedAt, 'h:mm a')}
                                </p>
                            </div>
                        }
                    </div>
                </div>
                <div className={`${shortMode ? 'flex flex-col' : 'w-3/12 pl-2'}`}>
                    {
                        offer?.treatmentOffered === null &&
                        <div className='leading-5'>
                            <p className='font-semibold pb-4'>
                                Schedule Offer Date
                            </p>
                            <p className='pb-5'>
                                {
                                    offer.scheduleOfferDate && isValidDate(offer.scheduleOfferDate)
                                        ? (
                                            <>
                                                {formatDate(offer.scheduleOfferDate)}
                                                <SlaBadge className="ml-3" satisfied={offer.satisfiedSla}/>
                                            </>
                                        ) : 'N/A'
                                }
                            </p>

                        </div>
                    }
                    {offer?.treatmentOffered !== null &&
                        <div className='leading-5'>
                            <p className='font-semibold pb-2'>
                                Treatment Offered
                            </p>
                            <p className='pb-5'>
                                {
                                    treatmentOfferedToLabel(offer?.treatmentOffered as TreatmentOffered)
                                }
                            </p>

                        </div>
                    }
                    {
                        shortMode ?
                            <div className='leading-5'>
                                <p className='font-semibold pb-2'>
                                    Offer Submitted Date
                                </p>
                                <p className='pb-5'>
                                    {
                                        <>
                                            {formatISODateStr(offer.submittedAt, ESBA_DATE_FORMAT)}
                                        </>
                                    }
                                </p>
                            </div> : <></>
                    }
                    <>
                        <div>
                            <p className='font-semibold pb-2'>
                                Provider Comments
                            </p>
                            <Pre wordBreak='break-word' whiteSpace='break-spaces' wordWrap='break-word'
                                 className='pb-5'
                                 fontSize='inherit' font='inherit'
                                 style={{
                                     color: 'inherit'
                                 }}
                            >
                                {offer.comments ? offer.comments : 'N/A'}
                            </Pre>
                            {
                                referralRequiredLanguage &&
                                <>
                                    <p className='font-semibold pb-2'>
                                        {`Provide Services in ${referralRequiredLanguage}`}
                                    </p>
                                    <p className='pb-5'>
                                        {
                                            offer.provideServiceInRequiredLanguage ? 'Yes' : 'No, interpreter required'
                                        }
                                    </p>
                                </>
                            }
                        </div>
                        {
                            offer?.draftStaffingPlan && !isEmpty(offer?.draftStaffingPlan.comments) &&
                            <div>
                                <p className='font-semibold pb-2'>
                                    Staffing Plan Comments
                                </p>
                                <Pre wordBreak='break-word' whiteSpace='break-spaces' wordWrap='break-word'
                                     className='pb-5'
                                     fontSize='inherit' font='inherit'
                                     style={{color: 'inherit'}}
                                >
                                    {offer.draftStaffingPlan.comments}
                                </Pre>
                            </div>
                        }
                    </>
                </div>
                <div className={`${shortMode ? '' : 'w-5/12 pl-2'} leading-4`}>
                    <div className={`${shortMode ? '' : 'flex'}`}>
                        {nonMedicatedRender(offer, psychotherapyAuthorizedHour)}
                        {caregiverMediatedRender(offer, locationCond)}
                        {practitionerMedicatedHoursRender(offer)}
                    </div>
                    {draftStaffingPlanRenderer(offer)}
                </div>
            </div>
        </div>
    );
}

const draftStaffingPlanRenderer = (offer: Offer) => {
    const plan = offer.draftStaffingPlan;
    if (!plan) return null;

    const renderPlanHours = (label: string, hours: number | null) => (
        hours && (
            <>
                <p className='pb-2'>{label}</p>
                <p>{hours}</p>
            </>
        )
    );

    return (
        <div className="w-full pt-4">
            <div className="font-semibold pb-4">Staffing Plan:</div>
            <div className="pb-2">
                <span>Full Offer Start Date:</span>
                <span className="pl-2">{formatISODateStr(plan.fullOfferStartDate ?? "")}</span>
            </div>
            {/*<div className="pb-4">*/}
            {/*    <span>Reviewed And Approved By Client Family Date:</span>*/}
            {/*    <span className="pl-2">{formatISODateStr(plan.reviewedAndApprovedByClientFamilyDate ?? "")}</span>*/}
            {/*</div>*/}
            <div className='grid grid-cols-2'>
                {renderPlanHours('Direct Care Hours', plan.offeredParaprofessionalHours)}
                {renderPlanHours('Mid-level Hours', plan.offeredMidLevelHours)}
                {renderPlanHours('High-level Hours', plan.offeredHighLevelHours)}
                {renderPlanHours('Group Care Hours', plan.offeredGroupCareHours)}
            </div>
        </div>
    );
}
const nonMedicatedRender = (offer: Offer, psyAuthorizedHour: PsychotherapyAuthorizedHour | null) => {
    if (offer.treatmentOffered !== null ||
        ((offer.midHours === null && offer.highHours === null) && (psyAuthorizedHour === null))
    ) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="font-semibold pb-4">
                Treatment Offered:
            </div>
            <div className='flex'>
                {psyAuthorizedHour !== null && offer.psychotherapyOffer !== null &&
                    <>
                        <div>
                            <p className='pb-2'>
                                Offer {psyAuthorizedHour.authTherapyTreatmentUnitType} per {psyAuthorizedHour.authTherapyTreatmentUnitsPer}:
                            </p>
                        </div>
                        <div className='pl-5'>
                            <p className={`pb-2 ${offer.psychotherapyOffer < Number(psyAuthorizedHour.authTherapyTreatmentUnit) ? 'text-red-600' : ''}`}>
                                {offer.psychotherapyOffer} {pluralize(psyAuthorizedHour.authTherapyTreatmentUnitType, offer.psychotherapyOffer)}
                            </p>
                        </div>
                    </>
                }
                {psyAuthorizedHour === null &&
                    <>
                        <div className=''>
                            {
                                offer.midHours &&
                                <p className='pb-2'>
                                    Mid-level Hours
                                </p>
                            }
                            {
                                offer.highHours &&
                                <p className='pb-2'>
                                    High-level Hours
                                </p>
                            }
                        </div>
                        <div className='pl-5'>
                            {
                                offer.midHours &&
                                <p className={`pb-2 ${offer.midHours < Number(offer.midHoursEst) ? 'text-red-600' : ''}`}>
                                    {offer.midHours}
                                </p>
                            }
                            {
                                offer.highHours &&
                                <p className={`pb-2 ${offer.highHours < Number(offer.highHoursEst) ? 'text-red-600' : ''}`}>
                                    {offer.highHours}
                                </p>
                            }
                        </div>
                    </>
                }
            </div>
        </div>
    );
}

// This block also runs for a caregiver-and-practitioner offer, and the practitioner block
// below already prints that offer's locations. So the two locations here only show for a
// plain caregiver offer.
const caregiverMediatedRender = (offer: Offer, locationCond: CaregiverLocationDisplay) => {
    if (offer.treatmentOffered == null || isTreatmentOfferedPractitionerMedicated(offer.treatmentOffered)) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="font-semibold pb-2">
                Caregiver Mediated:
            </div>
            <div className="pb-6">
                <span>Start date:</span>
                <span className="pl-2">
                    {formatISODateStr(offer.startDateCaregiverMediatedTreatment ?? "", ESBA_DATE_FORMAT)}
                </span>
                <div className="pt-4">
                    <SlaBadge satisfied={offer.satisfiedSla}/>
                </div>
            </div>
            <div className='grid grid-cols-2'>
                <p className='pb-2'>
                    Mid-level Hours
                </p>
                <p className={`pb-2 ${(offer.caregiverMediatedMidLevelHours ?? 0) < Number(offer.caregiverMediatedMidLevelHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.caregiverMediatedMidLevelHours}
                    {
                        locationCond.showMidLevel &&
                        <span className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationMidLevelType)}</span>
                    }
                </p>
                <p className='pb-2'>
                    High-level Hours
                </p>
                <p className={`pb-2 ${(offer.caregiverMediatedHighLevelHours ?? 0) < Number(offer.caregiverMediatedHighLevelHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.caregiverMediatedHighLevelHours}
                    {
                        locationCond.showHighLevel &&
                        <span className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationHighLevelType)}</span>
                    }
                </p>
            </div>
        </div>
    );
}
const practitionerMedicatedHoursRender = (offer: Offer) => {
    if (offer.treatmentOffered == null || isTreatmentOfferedCareMedicated(offer.treatmentOffered)) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="font-semibold pb-2">
                Practitioner Mediated:
            </div>
            <div className="pb-6">
                {offer.startDatePractMediatedTreatment &&
                    <>
                        <span>Start date:</span>
                        <span className="pl-2">
                            {formatISODateStr(offer.startDatePractMediatedTreatment ?? "")}
                        </span>
                    </>
                }
                {
                    isTreatmentOfferedPractitionerMedicated(offer.treatmentOffered) &&
                    <div className="pt-4">
                        {
                            isEmpty(offer.startDatePractMediatedTreatment) &&
                            offer.scheduleOfferDate && isValidDate(offer.scheduleOfferDate) &&
                            <>
                                <span className="pr-2">{formatDate(offer.scheduleOfferDate)}</span>
                            </>
                        }
                        <SlaBadge satisfied={offer.satisfiedSla}/>
                    </div>
                }
            </div>
            <div className='grid grid-cols-2'>
                <p className='pb-2'>
                    Direct Care Hours
                </p>
                <p className={`pb-2 ${offer.directCareHours < Number(offer.directCareHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.directCareHours} <span
                    className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationParaprofessionalType)}</span>
                </p>
                <p className='pb-2'>
                    Mid-level Hours
                </p>
                <p className={`pb-2 ${offer.midHours < Number(offer.midHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.midHours} <span
                    className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationMidLevelType)}</span>
                </p>
                <p className='pb-2'>
                    High-level Hours
                </p>
                <p className={`pb-2 ${offer.highHours < Number(offer.highHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.highHours} <span
                    className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationHighLevelType)}</span>
                </p>
                <p className='pb-2'>
                    Group Care Hours
                </p>
                <p className={`pb-2 ${offer.groupHours < Number(offer.groupHoursEst) ? 'text-red-600' : ''}`}>
                    {offer.groupHours} <span
                    className={'pl-1'}>{ServiceLocationTypeMap.get(offer.serviceLocationGroupCareType)}</span>
                </p>
            </div>

        </div>
    )
        ;
}
